//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import FeedLogModel from '../models/feedlog.model';
import FarmSiteModel from '../models/farm-site.model';

/**
 * @service FeedLogService
 * @description Service for managing feed log data and related aquaculture metrics.
 * This service provides HTTP methods to retrieve feed log activities, pond metrics,
 * site summaries, FCR (Feed Conversion Ratio) data, and perform tank shifting operations.
 * It interacts with the `/blubay/feedlog/` backend endpoint.
 * Extends the base Service class with the generic type FeedLogModel.
 *
 * @feature 1. Fetch all feed log activities with pagination.
 * @feature 2. Retrieve detailed pond metrics (ABW, FCR, etc.) for a specific culture.
 * @feature 3. Get aggregated site summaries and FCR summaries for multiple farm sites.
 * @feature 4. Fetch the latest feed log data for a given date and pond.
 * @feature 5. Retrieve feed logs associated with a specific batch number.
 * @feature 6. Perform tank shift operations to move animals between ponds.
 * @feature 7. Get activity data for specific persons within a date range.
 * @feature 8. Cancel ongoing HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends the base `Service<FeedLogModel>` to provide standardized REST API
 * integration. It uses Angular's `HttpClient` for all HTTP operations and manages
 * subscriptions in an `ongoingRequests` array for easy cancellation, ensuring
 * robust resource management.
 *
 * @service-endpoint /blubay/feedlog/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class FeedLogService extends Service<FeedLogModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * This is used to manage and cancel pending requests, for example, when a component is destroyed.
   * @default []
   * @side-effect Stores subscription references, which are later used by `cancelOnGoingHttpRequests()`.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor that injects the HttpClient and sets up the service endpoint.
   *
   * Calls the parent `Service` constructor, passing the `HttpClient` instance and the
   * specific API endpoint path for feed log operations.
   *
   * @param {HttpClient} httpClient - @service Angular's HTTP client for making API requests.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/feedlog/');
  }

  /**
   * @method getAllFeedLogActivities
   * @async
   * @description Fetches all feed log activities from the backend with pagination.
   *
   * Makes an HTTP GET request to retrieve a list of all feed log records, ordered by the newest first.
   *
   * @returns {Promise<Array<FeedLogModel>>} A promise that resolves with an array of feed log models.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP GET request and adds its subscription to the `ongoingRequests` array.
   *
   * @example
   * const activities = await feedLogService.getAllFeedLogActivities();
   * console.log('All feed activities:', activities);
   */
  public getAllFeedLogActivities() {
    return new Promise<Array<FeedLogModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FeedLogModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondsByFarmsites
   * @async
   * @description Retrieves detailed pond metrics for a specific pond and culture.
   *
   * Makes an HTTP GET request to the `pondMetrics` endpoint to fetch data like ABW, FCR,
   * survival rate, etc., for a given pond and its active culture.
   *
   * @param {string} pondId - The unique identifier for the pond.
   * @param {string} cultureId - The unique identifier for the culture within the pond.
   * @returns {Promise<FeedLogModel>} A promise that resolves with a single `FeedLogModel` containing pond metrics.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP GET request and adds its subscription to the `ongoingRequests` array.
   *
   * @example
   * const metrics = await feedLogService.getPondsByFarmsites('pond-123', 'culture-456');
   */
  public getPondsByFarmsites(pondId: string, cultureId: string) {
    return new Promise<FeedLogModel>((resolve, reject) => {
      const request = this.httpClient.get<FeedLogModel>(`${this.baseUrl}${this.serviceUrl}pondMetrics/pondId/${pondId}/pondCultureId/${cultureId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFCRSummary
   * @async
   * @description Fetches a summary of Feed Conversion Ratio (FCR) for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<FeedLogModel>>} A promise that resolves with an array of FCR summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getFCRSummary(farmSites: FarmSiteModel[]) {
    return new Promise<Array<FeedLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FeedLogModel>>(`${this.baseUrl}${this.serviceUrl}fcr/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFCRLocationSummary
   * @async
   * @description Fetches a summary of Feed Conversion Ratio (FCR) grouped by location for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<FeedLogModel>>} A promise that resolves with an array of location-based FCR summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getFCRLocationSummary(farmSites: FarmSiteModel[]) {
    return new Promise<Array<FeedLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FeedLogModel>>(`${this.baseUrl}${this.serviceUrl}fcr/location/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFCRDetails
   * @async
   * @description Fetches detailed Feed Conversion Ratio (FCR) data, including pond and section names, for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<FeedLogModel>>} A promise that resolves with an array of detailed FCR data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getFCRDetails(farmSites: FarmSiteModel[]) {
    return new Promise<Array<FeedLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FeedLogModel>>(`${this.baseUrl}${this.serviceUrl}pondName/sectionName/fcr/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSiteLevel
   * @async
   * @description Fetches a farm summary at the site level for a specific farm site and investor.
   *
   * @param {any} farmsiteId - The identifier for the farm site.
   * @param {any} investorId - The identifier for the farm investor.
   * @returns {Promise<FeedLogModel>} A promise that resolves with the farm summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getSiteLevel(farmsiteId, investorId) {
    return new Promise<FeedLogModel>((resolve, reject) => {
      const request = this.httpClient.post<FeedLogModel>(`${this.baseUrl}${this.serviceUrl}farmSummary/farmSite/farmInvestor`, {
        farmSite: farmsiteId, farmInvestorId: investorId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getActivityDate
   * @async
   * @description Fetches activity data for a given set of person IDs within a specified date range.
   *
   * @param {any} startDate - The start date of the range.
   * @param {any} endDate - The end date of the range.
   * @param {any} personIds - An array of person IDs to fetch activities for.
   * @returns {Promise<FeedLogModel>} A promise that resolves with the activity data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getActivityDate(startDate: any, endDate: any, personIds: any) {
    return new Promise<FeedLogModel>((resolve, reject) => {
      const request = this.httpClient.post<FeedLogModel>(`${this.baseUrl}${this.serviceUrl}activities/personID/date`, {
        fromDate: startDate, toDate: endDate, personId: personIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancels all ongoing/pending HTTP requests initiated by this service.
   *
   * Iterates through the `ongoingRequests` array and unsubscribes from each active
   * subscription. This is a crucial cleanup step to prevent memory leaks and
   * race conditions when a component is destroyed.
   *
   * @execution-flow
   * 1. Iterate through each subscription in the `ongoingRequests` array.
   * 2. Check if the subscription object is valid and has an `unsubscribe` function.
   * 3. Call `unsubscribe()` on each active subscription to abort the HTTP request.
   * 4. Clear the `ongoingRequests` array.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears the `ongoingRequests` array.
   * @usage This method should be called in the `ngOnDestroy` lifecycle hook of any
   * component that uses this service to ensure proper cleanup.
   */
   public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
